import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { AuthorizationService, USER_SECURITY_INFO_KEY } from 'services/authorization.service';
import { ToastService } from 'services/toast.service';
import { ClientCard, UpdateClientRequest } from 'types/client.interface';
import { CountryDto } from 'types/country.interface';
import { formatDatetime } from 'util/date.utility';
import {
  MAX_LENGTH_ADDRESS,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_NAME,
  MAX_LENGTH_PHONE,
  MIN_LENGTH_ADDRESS,
  MIN_LENGTH_EMAIL,
  MIN_LENGTH_NAME,
  MIN_LENGTH_PHONE,
  atLeastOneContactInfoRequired,
  onlyOneAuthFieldAllowed,
  countryIdValidator,
  emailValidator,
  getControlsWithErrors,
  phoneValidator
} from 'util/form.utility';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { BaseClientFormComponent } from '../base-client-form';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-general-info-form',
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, NgClass, SpinnerComponent, TooltipComponent, DatePickerComponent, ConfirmModalComponent],
  templateUrl: './general-info-form.component.html',
  styleUrls: ['../../form/base-form.scss', './general-info-form.component.scss']
})
export class GeneralInfoFormComponent extends BaseClientFormComponent implements OnInit {
  public readonly formatDatetime = formatDatetime;
  public readonly formatDate = formatDatetime;
  authenticationType = '';
  public readonly dateRangeMode = DateRangeMode;
  @Input() client!: ClientCard;
  @Input() countries: CountryDto[] = [];
  @Output() clientUpdated = new EventEmitter<ClientCard>();
  @Output() formChanged = new EventEmitter<boolean>();
  isLoading: boolean = false;
  isConfirmModalOpen: boolean = false;
  hasFormChanged: boolean = false;
  private formSub?: Subscription;
  get name(): AbstractControl<any, any> | null {
    return this.form.get('name');
  }
  get surname(): AbstractControl<any, any> | null {
    return this.form.get('surname');
  }
  get birthday(): AbstractControl<any, any> | null {
    return this.form.get('birthday');
  }
  get nationalDigitalId(): AbstractControl<any, any> | null {
    return this.form.get('nationalDigitalId');
  }
  get officialDocumentId(): AbstractControl<any, any> | null {
    return this.form.get('officialDocumentId');
  }
  get email(): AbstractControl<any, any> | null {
    return this.form.get('email');
  }
  get authEmail(): AbstractControl<any, any> | null {
    return this.form.get('authEmail');
  }
  get phone(): AbstractControl<any, any> | null {
    return this.form.get('phone');
  }
  get address(): AbstractControl<any, any> | null {
    return this.form.get('address');
  }
  get countryId(): AbstractControl<any, any> | null {
    return this.form.get('countryId');
  }
  get isIdVerifiedInPerson(): AbstractControl<any, any> | null {
    return this.form.get('isIdVerifiedInPerson');
  }
  get verifierFullName(): AbstractControl<any, any> | null {
    return this.form.get('verifierFullName');
  }

  constructor(
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    private toastService: ToastService,
    private authService: AuthorizationService
  ) {
    super(formBuilder, apiService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.authenticationType = this.client.authenticationType;
    this.bindFormDirtyWatcher();
  }

  private bindFormDirtyWatcher(): void {
    this.formSub?.unsubscribe();
    this.formSub = this.form.valueChanges.subscribe(() => {
      this.hasFormChanged = this.form.dirty;
      this.formChanged.emit(this.form.dirty);
    });
  }

  override setIsEditMode(isEditMode: boolean, confirmed: boolean = false): void {
    if (!confirmed && !isEditMode) {
      this.isConfirmModalOpen = true;
    } else {
      super.setIsEditMode(isEditMode);
      if (!isEditMode) {
        this.fillForm(); // creates a NEW FormGroup
        this.bindFormDirtyWatcher(); // <-- reattach subscription to the NEW form
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.formChanged.emit(false);
      }
    }
  }

  override fillForm(): void {
    this.form = this.formBuilder.group(
      {
        name: [this.client.name || '', [Validators.required, Validators.minLength(MIN_LENGTH_NAME), Validators.maxLength(MAX_LENGTH_NAME)]],
        agreementToPortalTerms: [this.client?.agreementToPortalTerms || false],
        surname: [
          this.client.surname || '',
          [Validators.required, Validators.minLength(MIN_LENGTH_NAME), Validators.maxLength(MAX_LENGTH_NAME)]
        ],
        birthday: [this.client.birthday || '', [Validators.required]],
        nationalDigitalId: [
          this.client?.nationalDigitalId,
          [
            Validators.pattern(/^\d*-?\d*$/),
            Validators.minLength(11),
            Validators.maxLength(12),
            ...(this.client?.authenticationType === 'NationalId' ? [Validators.required] : [])
          ]
        ],
        officialDocumentId: [this.client?.officialDocumentId, []],
        address: [this.client?.address || '', [Validators.minLength(MIN_LENGTH_ADDRESS), Validators.maxLength(MAX_LENGTH_ADDRESS)]],
        phone: [
          this.client?.phone || '',
          [phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]
        ],
        authEmail: [
          this.client?.authEmail || '',
          [
            emailValidator(),
            Validators.minLength(MIN_LENGTH_EMAIL),
            Validators.maxLength(MAX_LENGTH_EMAIL),
            ...(this.client?.authenticationType === 'Email' ? [Validators.required] : [])
          ]
        ],
        email: [
          this.client?.email || '',
          [emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
        ],
        countryId: [this.client.country?.id || '', [Validators.required, countryIdValidator()]],
        isIdVerifiedInPerson: [this.client.isIdVerifiedInPerson || false],
        verifiedById: [this.client?.verifiedBy?.id || null],
        verifierFullName: [
          this.client?.verifiedBy?.firstName && this.client?.verifiedBy?.lastName
            ? this.client?.verifiedBy?.firstName + ' ' + this.client?.verifiedBy?.lastName
            : ''
        ]
      },
      { validators: [atLeastOneContactInfoRequired(), onlyOneAuthFieldAllowed()] }
    );
    this.form.get('isIdVerifiedInPerson')?.valueChanges.subscribe((val: boolean) => {
      const officialDocumentIdControl = this.form.get('officialDocumentId');

      if (val) {
        officialDocumentIdControl?.setValidators([Validators.required]);
      } else {
        officialDocumentIdControl?.clearValidators();
      }

      officialDocumentIdControl?.updateValueAndValidity();

      const userSecurityInfo = localStorage.getItem(USER_SECURITY_INFO_KEY);
      this.form.get('verifiedById')?.setValue(val && userSecurityInfo ? JSON.parse(userSecurityInfo).userId : null);
      this.form.get('verifierFullName')?.setValue(val ? this.authService.getUserFullName() : '');
    });
    this.bindFormDirtyWatcher();
  }

  onCountryBlur(): void {
    const countryIdControl = this.form.get('countryId');
    if (countryIdControl) {
      const value = countryIdControl.value;
      countryIdControl.setValue(value === '' ? null : value);
    }
  }

  getVerifierFullName(): string {
    const { firstName = '', lastName = '' } = this.client.verifiedBy || {};
    return firstName && lastName ? `${firstName} ${lastName}` : '-';
  }

  override submit(): void {
    this.submitAttempted = true;

    if (this.form.valid) {
      this.isLoading = true;
      const args: UpdateClientRequest = {
        id: this.client.id,
        nationalDigitalId: this.nationalDigitalId?.value ? this.nationalDigitalId.value.replace(/-/g, '') : '',
        officialDocumentId: this.officialDocumentId?.value ? this.officialDocumentId.value.replace(/-/g, '') : '',
        clientType: this.client.clientType,
        status: this.client.status,
        ...this.form.value
      };

      this.apiService.updateClient(args).subscribe(
        (client: ClientCard) => {
          this.client = client;
          this.submitAttempted = false;
          this.isLoading = false;
          this.isEditMode = false;
          this.toastService.showSuccess('submitClientSuccess', '', {
            shouldTranslate: true
          });
          this.hasFormChanged = false;
          this.formChanged.emit(false);
          this.clientUpdated.emit(client);
        },
        (error: any) => {
          this.isLoading = false;

          try {
            const errorMsgOfficialDocumentId = error.error?.errors?.['officialDocumentId']?.[0]?.trim().toLowerCase();
            const errorMsgNationalDigitalId = error.error?.errors?.['nationalDigitalId']?.[0]?.trim().toLowerCase();
            const errorEmail = error.error?.errors?.['email']?.[0]?.trim().toLowerCase();
            const errorAuthEmail = error.error?.errors?.['authEmail']?.[0]?.trim().toLowerCase();
            const errorEmailInUse = error.error?.errors?.['Email is already in use']?.[0]?.trim().toLowerCase();
            const errorAuthEmailChangeForbidden = error.error?.errors?.['authEmail']?.[0]?.trim().toLowerCase();
            const errorBurthdayInFuturePresent = error.error?.errors?.['birthday']?.[0]?.trim().toLowerCase();

            if (errorMsgNationalDigitalId?.includes('already in use') || errorMsgOfficialDocumentId?.includes('already in use')) {
              this.toastService.showDanger('clientIdentifierExistsNationalId', '', {
                shouldTranslate: true
              });
            } else if (errorEmail?.includes('already in use') || errorAuthEmail?.includes('already in use')) {
            this.toastService.showDanger('emailAlreadyInUse', '', {
              shouldTranslate: true
            });
            } else if (errorBurthdayInFuturePresent?.includes('be in present time or future')) {
              this.toastService.showDanger('birthdayInvalid', '', {
                shouldTranslate: true
              });
            } else if (errorMsgNationalDigitalId?.includes('forbidden')) {
              this.toastService.showDanger('authTypeChangeForbidden', '', {
                shouldTranslate: true
              });
            } else if (errorMsgOfficialDocumentId?.includes('already exists')) {
              this.toastService.showDanger('clientIdentifierExistsOfficialId', '', {
                shouldTranslate: true
              });
            } else if (errorAuthEmailChangeForbidden?.includes('forbidden')) {
              this.toastService.showDanger('authTypeChangeForbidden', '', {
                shouldTranslate: true
              });
            } else if (errorEmailInUse?.length) {
              this.toastService.showDanger('emailAlreadyInUse', '', {
                shouldTranslate: true
              });
            } else {
              this.toastService.showRequestError();
            }
          } catch {
            this.toastService.showRequestError();
          }
          console.error(error);
          this.isLoading = false;
        }
      );
    } else {
      const atLeastOneError = this.form.errors?.['atLeastOneContactInfoRequired'];
      if (atLeastOneError) {
        this.toastService.showDanger('contactInfoError', '', {
          shouldTranslate: true
        });
      }

      const onlyOneAuthField = this.form.errors?.['onlyOneAuthFieldAllowed'];
      if (onlyOneAuthField) {
        this.toastService.showDanger('onlyOneAuthFieldAllowedError', '', {
          shouldTranslate: true
        });
      }

      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.client');
    }
  }

  resendEmailVerification() {
    this.apiService.resendClientActivationEmail(this.client.id!).subscribe(
      () => {
        this.toastService.showSuccess('resendEmailSuccess', '', {
          shouldTranslate: true
        });
      },
      (error: any) => {
        console.error(error);
        this.isLoading = false;
        this.toastService.showRequestError();
      }
    );
  }

  allowOnlyNumbersAndSingleDash(event: KeyboardEvent) {
    const allowed = /[0-9-]/;
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }

    if (event.key === '-' && (event.target as HTMLInputElement).value.includes('-')) {
      event.preventDefault();
    }
  }

  validateSingleDash(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/(.*-.*)-+/, '$1'); // keep only first dash
  }
}
